import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SlidersHorizontal, ChevronDown, X, Check } from "lucide-react";
import { useLocations } from "@/hooks/useLocations";
import { sharedApi } from "@/lib/api";

/**
 * Iter-374/376 — TradeMe-style Refine UI for the Property category.
 * Iter-376 founder corrections:
 *   1. Collapsed by default — "Refine ▾" opens the full panel; each rail
 *      chip opens a small popover with ONLY that section.
 *   2. Price = Min/Max dropdowns with preset steps (sale $100k→$10M,
 *      rent weekly $300→$1000); Max options below Min are disabled.
 *   3. Location = Region → City → Suburb cascade
 *      (GET /classifieds/areas?region&city; "All Suburbs" omits `area`).
 * All controls write straight to the URL query (shareable).
 */

const BEDROOM_STEPS = ["1", "2", "3", "4", "5", "6"];
const PARKING_STEPS = ["1", "2", "3"];
const TYPE_OPTS = ["Apartments", "Houses & Villas", "Builder Floors", "Farm House"];
const FURNISHING_OPTS = ["Furnished", "Semi-Furnished", "Unfurnished"];
const CONSTRUCTION_OPTS = ["Ready to Move", "New Launch", "Under Construction"];
const LISTED_BY_OPTS = ["Owner", "Dealer", "Builder"];

const SALE_STEPS = [
  100000, 200000, 300000, 400000, 500000, 600000, 700000, 800000, 900000,
  1000000, 1250000, 1500000, 2000000, 3000000, 5000000, 10000000,
];
const RENT_STEPS = [300, 350, 400, 450, 500, 550, 600, 650, 700, 750, 800, 850, 900, 950, 1000];

function priceLabel(n, rent) {
  if (rent) return `$${n}${n >= 1000 ? "+" : ""}`;
  if (n >= 1000000) return `$${(n / 1000000).toFixed(n % 1000000 ? 2 : 0).replace(/\.?0+$/, "")}M`;
  return `$${n / 1000}k`;
}

// "3" → "3,4,5,6,6+" (csv from N upward, per the workorder)
export function bedroomCsv(n) {
  return [...BEDROOM_STEPS.filter((s) => Number(s) >= Number(n)), "6+"].join(",");
}
export function parkingCsv(n) {
  return [...PARKING_STEPS.filter((s) => Number(s) >= Number(n)), "4+"].join(",");
}
export function csvToN(v) {
  const first = String(v || "").split(",")[0].replace("+", "");
  return first || "";
}

function Row({ label, children }) {
  return (
    <div className="py-3 border-b border-slate-100 last:border-0">
      <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function Pill({ on, onClick, children }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${
        on ? "bg-[color:var(--brand-blue)] text-white border-transparent" : "bg-white text-slate-600 border-soft hover:border-[color:var(--brand-blue)]"
      }`}
    >
      {children}
    </button>
  );
}

function StepChips({ testId, value, onPick, steps }) {
  const current = csvToN(value);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      <Pill on={!current} onClick={() => onPick("")}>Any</Pill>
      {steps.map((s) => (
        <Pill key={s} on={current === s} onClick={() => onPick(s)}>{s}+</Pill>
      ))}
    </div>
  );
}

function MultiChecks({ testId, param, params, applyFilters, options }) {
  const selected = (params.get(param) || "").split(",").map((s) => s.trim()).filter(Boolean);
  const toggle = (opt) => {
    const next = selected.includes(opt) ? selected.filter((s) => s !== opt) : [...selected, opt];
    applyFilters({ [param]: next.join(",") });
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map((opt) => (
        <Pill key={opt} on={selected.includes(opt)} onClick={() => toggle(opt)}>{opt}</Pill>
      ))}
    </div>
  );
}

function RangeInputs({ testId, minKey, maxKey, params, applyFilters, unit }) {
  const [min, setMin] = useState(params.get(minKey) || "");
  const [max, setMax] = useState(params.get(maxKey) || "");
  const commit = () => applyFilters({ [minKey]: min.trim(), [maxKey]: max.trim() });
  const onKey = (e) => e.key === "Enter" && (e.preventDefault(), commit());
  const cls = "w-24 rounded-lg border border-soft bg-white px-2.5 py-1.5 text-xs focus:outline-none focus:border-[color:var(--brand-blue)]";
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <input value={min} onChange={(e) => setMin(e.target.value.replace(/[^0-9]/g, ""))} onBlur={commit} onKeyDown={onKey} placeholder="Min" inputMode="numeric" className={cls} data-testid={`${testId}-min`} />
      <span className="text-slate-300">—</span>
      <input value={max} onChange={(e) => setMax(e.target.value.replace(/[^0-9]/g, ""))} onBlur={commit} onKeyDown={onKey} placeholder="Max" inputMode="numeric" className={cls} data-testid={`${testId}-max`} />
      {unit && <span className="text-[11px] text-slate-400 font-semibold">{unit}</span>}
    </div>
  );
}

export default function PropertyRefine({ params, applyFilters, total, loading }) {
  // null = collapsed (default) · "all" = full panel · section key = popover
  const [openSection, setOpenSection] = useState(null);
  const { data: regions = [] } = useLocations();
  const region = params.get("region") || "";
  const city = params.get("city") || "";
  const area = params.get("area") || "";
  const cities = regions.find((r) => r.region === region)?.cities || [];
  const cityNames = cities.map((c) => (typeof c === "string" ? c : c.city)).filter(Boolean);

  // Iter-376 §3 — suburb drill-down (verified live endpoint)
  const areasQ = useQuery({
    queryKey: ["areas", region, city],
    enabled: !!region && !!city,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/areas", { params: { region, city } });
      return Array.isArray(data) ? data : data?.areas || [];
    },
  });
  const suburbs = (areasQ.data || [])
    .map((a) => (typeof a === "string" ? a : a.area))
    // API includes the "All Suburbs" sentinel — we render our own option.
    .filter((s) => s && s !== "All Suburbs");

  const isRent = (params.get("category") || "").includes("rent");

  // Iter-531 — property range filters come from the shared filter schema.
  const schemaQ = useQuery({
    queryKey: ["filter-schema", params.get("category") || "property"],
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/filter-schema", {
        params: { slug: params.get("category") || "property" },
      });
      return data;
    },
  });
  const priceSteps = isRent ? RENT_STEPS : SALE_STEPS;
  const minPrice = params.get("min_price") || "";
  const maxPrice = params.get("max_price") || "";

  const beds = csvToN(params.get("attr.Bedrooms"));
  const baths = csvToN(params.get("attr.Bathrooms"));
  const typeVal = params.get("attr.Type") || "";
  const priceSet = minPrice || maxPrice;

  const clearAll = () => {
    const patch = {};
    params.forEach((v, k) => {
      if (k !== "category") patch[k] = "";
    });
    applyFilters(patch);
  };

  const priceSelect = (key, value, label, testid) => (
    <select
      value={value}
      onChange={(e) => applyFilters({ [key]: e.target.value })}
      data-testid={testid}
      className="rounded-lg border border-soft bg-white px-2.5 py-1.5 text-xs focus:outline-none"
    >
      <option value="">{label}</option>
      {priceSteps.map((n) => (
        <option
          key={n}
          value={n}
          disabled={key === "max_price" && !!minPrice && n < Number(minPrice)}
        >
          {priceLabel(n, isRent)}
        </option>
      ))}
    </select>
  );

  /* Section renderers — shared by the full panel and the chip popovers */
  const sections = {
    location: (
      <Row label="Location">
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={region}
            onChange={(e) => applyFilters({ region: e.target.value, city: "", area: "" })}
            data-testid="refine-region"
            className="rounded-lg border border-soft bg-white px-2.5 py-1.5 text-xs focus:outline-none"
          >
            <option value="">All of NZ</option>
            {regions.map((r) => (
              <option key={r.region} value={r.region}>{r.region}</option>
            ))}
          </select>
          {!!cityNames.length && (
            <select
              value={city}
              onChange={(e) => applyFilters({ city: e.target.value, area: "" })}
              data-testid="refine-city"
              className="rounded-lg border border-soft bg-white px-2.5 py-1.5 text-xs focus:outline-none"
            >
              <option value="">All {region}</option>
              {cityNames.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}
          {!!city && (
            <select
              value={area}
              onChange={(e) => applyFilters({ area: e.target.value })}
              data-testid="refine-suburb"
              className="rounded-lg border border-soft bg-white px-2.5 py-1.5 text-xs focus:outline-none"
            >
              <option value="">All Suburbs</option>
              {suburbs.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}
        </div>
      </Row>
    ),
    price: (
      <Row label={isRent ? "Rent per week ($)" : "Price ($)"}>
        <div className="flex items-center gap-2" data-testid="refine-price">
          {priceSelect("min_price", minPrice, "Min (any)", "refine-price-min")}
          <span className="text-slate-300">—</span>
          {priceSelect("max_price", maxPrice, "Max (any)", "refine-price-max")}
        </div>
      </Row>
    ),
    bedrooms: (
      <Row label="Bedrooms">
        <StepChips testId="refine-bedrooms" value={params.get("attr.Bedrooms")} steps={BEDROOM_STEPS} onPick={(n) => applyFilters({ "attr.Bedrooms": n ? bedroomCsv(n) : "" })} />
      </Row>
    ),
    bathrooms: (
      <Row label="Bathrooms">
        <StepChips testId="refine-bathrooms" value={params.get("attr.Bathrooms")} steps={BEDROOM_STEPS} onPick={(n) => applyFilters({ "attr.Bathrooms": n ? bedroomCsv(n) : "" })} />
      </Row>
    ),
    type: (
      <Row label="Property type">
        <MultiChecks testId="refine-type" param="attr.Type" params={params} applyFilters={applyFilters} options={TYPE_OPTS} />
      </Row>
    ),
  };

  const chip = (section, label, active, testid) => (
    <button
      type="button"
      data-testid={testid}
      onClick={() => setOpenSection((s) => (s === section ? null : section))}
      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${
        active ? "bg-slate-900 text-white border-transparent" : "bg-white text-slate-600 border-soft hover:border-slate-400"
      }`}
    >
      {label} <ChevronDown className={`w-3 h-3 transition-transform ${openSection === section ? "rotate-180" : ""}`} />
    </button>
  );

  return (
    <div className="mt-3" data-testid="property-refine">
      {/* Chip rail — collapsed by default */}
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          data-testid="refine-toggle"
          onClick={() => setOpenSection((s) => (s === "all" ? null : "all"))}
          className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-blue)] text-white px-3.5 py-1 text-xs font-bold transition-colors hover:bg-[color:var(--brand-blue-hover)]"
        >
          <SlidersHorizontal className="w-3.5 h-3.5" /> Refine
          <ChevronDown className={`w-3 h-3 transition-transform ${openSection === "all" ? "rotate-180" : ""}`} />
        </button>
        {chip("location", area || city || region || "Location", !!region, "refine-chip-location")}
        {chip("price", priceSet ? `${minPrice ? priceLabel(Number(minPrice), isRent) : "Any"}–${maxPrice ? priceLabel(Number(maxPrice), isRent) : "any"}` : "Price", !!priceSet, "refine-chip-price")}
        {chip("bedrooms", beds ? `${beds}+ beds` : "Bedrooms", !!beds, "refine-chip-bedrooms")}
        {chip("bathrooms", baths ? `${baths}+ baths` : "Bathrooms", !!baths, "refine-chip-bathrooms")}
        {chip("type", typeVal ? typeVal.split(",")[0] + (typeVal.includes(",") ? "…" : "") : "Property type", !!typeVal, "refine-chip-type")}
      </div>

      {/* Iter-376 §1 — per-chip anchored popover with just that section */}
      {openSection && openSection !== "all" && (
        <div className="relative">
          <div className="absolute z-30 mt-2 w-[22rem] max-w-[92vw] rounded-2xl bg-white shadow-lg border border-slate-200 px-4 py-1" data-testid="refine-popover">
            {sections[openSection]}
            <div className="py-2 flex justify-end">
              <button
                type="button"
                onClick={() => setOpenSection(null)}
                data-testid="refine-popover-done"
                className="inline-flex items-center gap-1 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-bold px-4 py-1.5 transition-colors"
              >
                <Check className="w-3 h-3" /> Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full panel via Refine ▾ */}
      {openSection === "all" && (
        <div className="mt-2 rounded-2xl bg-white shadow-card border border-slate-100 px-5 py-2" data-testid="refine-panel">
          {sections.location}
          {sections.price}
          {sections.bedrooms}
          {sections.bathrooms}
          {sections.type}
          <Row label="Land area (m²)">
            <RangeInputs testId="refine-land" minKey="min_land_area" maxKey="max_land_area" params={params} applyFilters={applyFilters} unit="m²" />
          </Row>
          <Row label="Floor area (m²)">
            <RangeInputs testId="refine-floor" minKey="min_floor_area" maxKey="max_floor_area" params={params} applyFilters={applyFilters} unit="m²" />
          </Row>
          <Row label="Parking">
            <StepChips testId="refine-parking" value={params.get("attr.Car Parking")} steps={PARKING_STEPS} onPick={(n) => applyFilters({ "attr.Car Parking": n ? parkingCsv(n) : "" })} />
          </Row>
          <Row label="Furnishing">
            <MultiChecks testId="refine-furnishing" param="attr.Furnishing" params={params} applyFilters={applyFilters} options={FURNISHING_OPTS} />
          </Row>
          <Row label="Construction status">
            <MultiChecks testId="refine-construction" param="attr.Construction Status" params={params} applyFilters={applyFilters} options={CONSTRUCTION_OPTS} />
          </Row>
          <Row label="Listed by">
            <MultiChecks testId="refine-listedby" param="attr.Listed By" params={params} applyFilters={applyFilters} options={LISTED_BY_OPTS} />
          </Row>
          {/* Iter-531 — remaining schema-declared ranges (Maintenance,
              Floor No, Total Floors…) via attrmin./attrmax. params.
              Floor/Land area keep their dedicated rows above. */}
          {(schemaQ.data?.range_filters || [])
            .filter((f) => !/floor area|land area/i.test(f.key))
            .map((f) => {
              const slug = String(f.key).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
              return (
                <Row key={f.key} label={f.label || f.key}>
                  <RangeInputs
                    testId={`refine-range-${slug}`}
                    minKey={f.param_min}
                    maxKey={f.param_max}
                    params={params}
                    applyFilters={applyFilters}
                  />
                </Row>
              );
            })}
          <Row label="Only show">
            <div className="flex flex-wrap gap-1.5">
              <Pill on={params.get("open_home") === "yes"} onClick={() => applyFilters({ open_home: params.get("open_home") === "yes" ? "" : "yes" })}>
                Has open home
              </Pill>
              <Pill on={params.get("attr.New Home") === "Yes"} onClick={() => applyFilters({ "attr.New Home": params.get("attr.New Home") === "Yes" ? "" : "Yes" })}>
                New homes only
              </Pill>
            </div>
          </Row>
          <div className="py-3 flex items-center justify-between">
            <button type="button" onClick={clearAll} data-testid="refine-clear-all" className="text-xs font-bold text-slate-500 hover:text-red-600 inline-flex items-center gap-1">
              <X className="w-3.5 h-3.5" /> Clear all refinements
            </button>
            <button
              type="button"
              onClick={() => setOpenSection(null)}
              data-testid="refine-show-results"
              className="rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-5 py-2 transition-colors"
            >
              {loading ? "Loading…" : `Show ${total ?? 0} result${total === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
